import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Modal,
  Typography,
  Input,
  Tabs,
  List,
  Card,
  Tag,
  Button,
  Space,
  Empty,
  Spin,
  Pagination,
  Tooltip,
} from "antd";
import {
  EyeOutlined,
  CopyOutlined,
  GlobalOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import "./SharedTherapiesModal.scss";

const { Title, Text } = Typography;

// Estas dos ayudantes viven fuera del componente, asi que reciben `t` como
// argumento en vez de llamar al hook: un hook solo puede usarse dentro de un
// componente.
function getVisibilityTag(visibility, t) {
  switch (visibility) {
    case "global":
      return (
        <Tag icon={<GlobalOutlined />} color="purple">
          {t("compartidas.visibilidadGlobal")}
        </Tag>
      );
    case "public":
      return (
        <Tag icon={<TeamOutlined />} color="blue">
          {t("compartidas.visibilidadInstitucion")}
        </Tag>
      );
    case "private":
    default:
      return (
        <Tag icon={<UserOutlined />} color="default">
          {t("compartidas.visibilidadPrivada")}
        </Tag>
      );
  }
}

function resolveAuthorName(author, t) {
  if (!author) return t("compartidas.autorDesconocido");
  if (typeof author === "string") return author;
  return (
    author.fullName ||
    author.name ||
    author.username ||
    author.email ||
    author._id ||
    t("compartidas.autorDesconocido")
  );
}

export function SharedTherapiesModal({
  isOpen,
  onClose,
  loading,
  therapies,
  page,
  pageSize,
  total,
  onPageChange,
  onSearch,
  onView,
  onClone,
}) {
  // "Ver" ya existe en el bloque comun, asi que esta pantalla usa los dos
  // namespaces en vez de repetir la cadena.
  const { t } = useTranslation(["rooms", "common"]);

  const [tab, setTab] = useState("all");
  const [cloningId, setCloningId] = useState(null);

  const counts = useMemo(() => {
    const all = therapies?.length || 0;
    const globals = (therapies || []).filter(
      (t) => t.visibility === "global",
    ).length;
    const publics = (therapies || []).filter(
      (t) => t.visibility === "public",
    ).length;
    return { all, globals, publics };
  }, [therapies]);

  const filtered = useMemo(() => {
    if (!Array.isArray(therapies)) return [];
    if (tab === "global")
      return therapies.filter((t) => t.visibility === "global");
    if (tab === "public")
      return therapies.filter((t) => t.visibility === "public");
    return therapies;
  }, [therapies, tab]);

  const handleClone = async (therapy) => {
    try {
      setCloningId(therapy?._id);
      await onClone?.(therapy);
    } finally {
      setCloningId(null);
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={1100}
      centered
      className="revi-shared-therapies-modal"
      destroyOnHidden
    >
      <div className="shared-modal-header">
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {t("compartidas.titulo")}
          </Title>
          <Text type="secondary" className="revi-subtitulo-pagina">
            {t("compartidas.subtitulo")}
          </Text>
        </div>

        <div className="shared-modal-actions">
          <Input.Search
            placeholder={t("compartidas.buscarPlaceholder")}
            allowClear
            onSearch={(value) => onSearch?.(value)}
            className="shared-modal-search"
          />
        </div>
      </div>

      {/*
        El contador va DENTRO de la plantilla, no pegado detras: en frances la
        etiqueta concuerda en genero ("Toutes") y el parentesis lleva su propio
        espaciado.
      */}
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: "all",
            label: t("compartidas.tabs.todas", { count: counts.all }),
          },
          {
            key: "global",
            label: t("compartidas.tabs.globales", { count: counts.globals }),
          },
          {
            key: "public",
            label: t("compartidas.tabs.institucion", { count: counts.publics }),
          },
        ]}
      />

      <div className="shared-modal-body">
        {loading ? (
          <div className="shared-loading">
            <Spin size="large" />
          </div>
        ) : filtered.length === 0 ? (
          <Empty description={t("compartidas.vacio")} />
        ) : (
          <List
            grid={{
              gutter: 16,
              xs: 1,
              sm: 1,
              md: 2,
              lg: 3,
              xl: 3,
              xxl: 3,
            }}
            dataSource={filtered}
            renderItem={(therapy) => {
              const coverImage =
                therapy?.image ||
                "https://via.placeholder.com/420x220?text=Sin+Portada";

              const authorName = resolveAuthorName(therapy?.author, t);

              return (
                <List.Item>
                  <Card
                    hoverable
                    className="shared-therapy-card"
                    cover={
                      <div className="shared-card-cover">
                        <img
                          src={coverImage}
                          alt={therapy?.title || t("compartidas.altPortada")}
                        />
                        <div className="shared-card-badge">
                          {getVisibilityTag(therapy?.visibility, t)}
                        </div>
                      </div>
                    }
                  >
                    <div className="shared-card-content">
                      <Tooltip title={therapy?.title}>
                        <Text strong className="shared-card-title" ellipsis>
                          {therapy?.title || t("compartidas.terapiaSinTitulo")}
                        </Text>
                      </Tooltip>

                      {/*
                        Contador de verdad —con una sesion decia "1 sesiones"—
                        y el autor dentro de la frase, porque en frances el
                        participio concuerda con "thérapie" y va en otro sitio.
                      */}
                      <div className="shared-card-meta">
                        <Text type="secondary">
                          {t("common:contadores.sesiones", {
                            count: therapy?.scenes?.length || 0,
                          })}
                        </Text>
                        <Text type="secondary">•</Text>
                        <Text type="secondary">
                          {t("compartidas.creadaPor", { autor: authorName })}
                        </Text>
                      </div>

                      <div className="shared-card-footer">
                        <Space>
                          <Button
                            icon={<EyeOutlined />}
                            onClick={() => onView?.(therapy)}
                          >
                            {t("common:actions.view")}
                          </Button>
                          <Button
                            type="primary"
                            icon={<CopyOutlined />}
                            loading={cloningId === therapy?._id}
                            onClick={() => handleClone(therapy)}
                          >
                            {t("compartidas.clonar")}
                          </Button>
                        </Space>
                      </div>
                    </div>
                  </Card>
                </List.Item>
              );
            }}
          />
        )}
      </div>

      <div className="shared-modal-pagination">
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          showSizeChanger={false}
          onChange={(p) => onPageChange?.(p)}
        />
      </div>
    </Modal>
  );
}
